import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import Card from '@/components/projects/cards/Card';
import ProjectCardsFactory from '@/components/projects/cards/factory/ProjectCardsFactory';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { ProjectFilters } from '@/data/services/projectServices';
import { projectsQuerykeys } from '@/data/services/querykeys';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useOutletContext } from 'react-router-dom';
import ProjectSectionHeaderPage from './ProjectSectionHeaderPage';
import useManualQuery from '@/data/hook/query/v1/useManualQuery';
import withLeiFelca from '@/components/projects/withLeiFelca';

function ProjectSectionPage() {
  const { filters: headerFilters } = useOutletContext<{
    filters: ProjectFilters;
  }>();

  const { t } = useTranslation('translation', {
    keyPrefix: 'projects.page',
  });
  const [sectionFilters, setSectionFilters] = useState({});

  const { state } = useLocation();

  const { filters: stateFilters, section } = state;

  const allFilters = {
    ...stateFilters,
    ...headerFilters,
    ...sectionFilters,
  };

  const {
    results: projects,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
  } = useInfiniteService({
    ...projectsQuerykeys.list(allFilters)._ctx.infinity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const { update } = useManualQuery({
    queryKey: projectsQuerykeys.list(allFilters)._ctx.infinity.queryKey,
  });

  const loadingCards = Array.from({ length: 20 }).map((_, index) => (
    <Card.Skeleton key={index} className="w-full" />
  ));

  return (
    <div className="flex flex-col">
      <ProjectSectionHeaderPage
        section={section}
        filters={allFilters}
        onFilter={setSectionFilters}
      />
      <ConditionalRenderer condition={!projects.length && !isLoading}>
        <div className="flex w-full justify-center items-center h-72">
          <Text text={t('noProjectsFound')} />
        </div>
      </ConditionalRenderer>
      <InfinityList
        hasNextPage={hasNextPage}
        onReachEnd={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2.5"
      >
        <ConditionalRenderer condition={!isLoading} fallback={loadingCards}>
          {projects.map(project => (
            <ProjectCardsFactory
              project={project}
              key={project.id}
              onMutate={update}
              mode="general"
              className={{
                base: 'w-full min-w-full xl:min-w-full',
              }}
            />
          ))}
        </ConditionalRenderer>
      </InfinityList>
    </div>
  );
}

export default withLeiFelca(ProjectSectionPage);
